(ns dashboard.views.questionnaire.components.archived-list
  (:require [re-frame.core :as rf]
            [dashboard.effects :as effects]
            [dashboard.services.http-config :as http-config]
            [dashboard.ui :as ui]
            [dashboard.notifications :as notifications]
            [dashboard.components.panel :refer [panel]]))

(def -component-id :component/questionnaire-archive-list)

(rf/reg-event-fx
  ::fetch-archived-list
  (fn [{:keys [db]} [_ questionnaire-id]]
    {:db       (update-in db [-component-id questionnaire-id] merge {:fetching? true})
     :http-xhrio {:method :get
                  :uri (str "/api/questionnaires/" questionnaire-id "/archive")
                  :response-format http-config/transit-response-format
                  :on-success [::fetch-archived-list-success questionnaire-id]
                  :on-failure [::ui/set-error "Could not fetch archives"]}}))

(rf/reg-event-db
 ::fetch-archived-list-success
 (fn [db [_ questionnaire-id archives]]
   (update-in db  [-component-id questionnaire-id] merge {:fetching? false
                                                :items archives})))

;; Archived questionnaires are in a private bucket.
;; To download fetch a presigned S3 URL
(rf/reg-event-fx
  ::archived.get-download-link
  (fn [_ [_ file-key]]
    {:http-xhrio (merge http-config/defaults
                        {:method     :get
                         :uri        "/api/archive/download"
                         :url-params {:filename file-key}
                         :on-success [::get-download-link.success]
                         :on-failure [::bad-http-result]})}))

(rf/reg-event-fx
  ::get-download-link.success
  (fn [_ [_ {:keys [url]}]]
    {::effects/fx-prompt-file-download url}))

(rf/reg-event-fx
 ::create-archive
 (fn [{:keys [db]} [_ questionnaire-id archive-type]]
   {:db (assoc-in db [-component-id questionnaire-id :archiving?] true)
    :http-xhrio {:method :post
                 :uri (str "/api/questionnaires/" questionnaire-id "/archive")
                 :format http-config/transit-request-format
                 :response-format http-config/transit-response-format
                 :params {:type archive-type}
                 :on-success [::create-archive-success questionnaire-id]
                 :on-failure [::create-archive-fail questionnaire-id]}}))

(rf/reg-event-fx
  ::create-archive-success
  (fn [{:keys [db]} [_ questionnaire-id archive]]
    {:db (-> db
             (assoc-in [-component-id questionnaire-id :archiving?] false)
             (update-in [-component-id questionnaire-id :items]
                        (fn [archives]
                          (sort-by :created #(compare %2 %1) (conj archives archive)))))
     :dispatch [::notifications/add {:message "Archive created"}]}))

(rf/reg-event-fx
 ::create-archive-fail
 (fn [{:keys [db]} [_ questionnaire-id response]]
   {:db (assoc-in db [-component-id questionnaire-id :archiving?] false)
    :dispatch [::ui/set-error (or (-> response :response :message)
                                  "Failed to create archive")]}))

(rf/reg-event-db
 ::toggle
 (fn [db [_ questionnaire-id]]
   (let [visible? (get-in db [-component-id questionnaire-id :visible?] false)]
     (assoc-in db [-component-id questionnaire-id :visible?] (not visible?)))))

(rf/reg-event-db
 ::open
 (fn [db [_ questionnaire-id]]
   (assoc-in db [-component-id questionnaire-id :visible?] true)))

(rf/reg-event-db
  ::set-archive-type
  (fn [db [_ questionnaire-id archive-type]]
    (assoc-in db [-component-id questionnaire-id :archive-type] archive-type)))

(rf/reg-sub
  ::state
  (fn [db [_ questionnaire-id]]
    (get-in db [-component-id questionnaire-id])))

(defn row-component [on-download archive-item]
  (let [{:keys [id created file author]
         archive-type :type} archive-item]
    [:tr {:key id}
     [:td
      [:span (case archive-type
               "workbook" "Excel"
               "csv" "CSV")]]
     [:td (->> created (.parse js/Date) (js/Date.) (.toLocaleString))]
     [:td author]
     [:td.is-narrow
      [:button.button.is-small.is-rounded.is-secondary
       {:on-click #(on-download file)
        :title "Download"}
       [:span.icon
        [:i.fas.fa-download]]]]]))

(defn archived-list
  [questionnaire-type questionnaire-id]
  (let [{:keys [items visible? archiving? fetching? archive-type]} @(rf/subscribe [::state questionnaire-id])
        workbook-archive-allowed? (= "workbook" questionnaire-type)
        archive-type (or archive-type
                         (if workbook-archive-allowed?
                           "workbook"
                           "csv"))]
    [panel {:class ["is-tertiary mb-1 has-no-shadow"]
            :panel-class :has-background-highlight
            :title [:<>
                    [:span "Archives (filled questionnaires)"]
                    (when fetching? [:div.loader.ml-3])]
            :actions [:<>
                      [:button.button.is-small.is-rounded.is-secondary
                       {:class (when archiving? "is-loading")
                        :disabled (or archiving? fetching?)
                        :on-click (fn [_]
                                    (rf/dispatch [::create-archive questionnaire-id archive-type])
                                    (rf/dispatch [::open questionnaire-id]))
                        :title "Create an archive with answers of approved questions"}
                       "Create Archive"]
                      [:div.ml-4.is-size-7
                       [:div {:style {:display "flex"
                                      :align-items "center"}}
                        [:label {:for "archive-type"}
                         [:span "Type: "]]
                        [:div.select.is-rounded.ml-2
                         [:select
                          {:id "archive-type"
                           :value archive-type
                           :on-change (fn [e]
                                        (rf/dispatch [::set-archive-type questionnaire-id (.. e -target -value)]))
                           :title "Select the type of archive to create"}
                          (when workbook-archive-allowed?
                            [:option {:value "workbook"
                                      :title "Excel file with answers fields filled in"}
                             "Excel"])
                          [:option {:value "csv"
                                    :title "CSV file with a row for each question in each section, with response and comment"}
                           "CSV"]]]]]
                      [:button.button.is-small.is-rounded.ml-2.is-secondary
                       {:on-click #(rf/dispatch [::toggle questionnaire-id])
                        :title "Show previously created archives"}
                       [:span.icon>i.fas {:class (if visible? "fa-chevron-up" "fa-chevron-down")}]]]}
     (when visible?
       [:div.p-0
        (if (seq items)
          [:table.table.is-striped.is-fullwidth
           [:thead.has-text-weight-bold
            [:tr
             [:td "Type"]
             [:td "Created"]
             [:td "By"]
             [:td ""]]]
           [:tbody
            (map (partial row-component
                          (fn [file]
                            (rf/dispatch
                              [::archived.get-download-link file])))
                 items)]]
          [:span.panel-block.p-2 "No results"])])]))
